// Gerado por: GPT — 04/09/2026
import { createClient } from '@supabase/supabase-js'
import type { AppData, Profile } from '../types'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined
export const isConfigured = Boolean(url && key && !url.includes('SEU-PROJETO'))
export const supabase = isConfigured ? createClient(url!, key!) : null

export async function signIn(email: string, password: string) {
  if (!supabase) throw new Error('Supabase ainda não conectado. Use o modo de demonstração.')
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
}

export async function requestPasswordReset(email: string) {
  if (!supabase) throw new Error('Supabase ainda não conectado.')
  const redirectTo = `${window.location.origin}/`
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo })
  if (error) throw error
}

export async function signUp(displayName: string, email: string, password: string) {
  if (!supabase) throw new Error('Supabase ainda não conectado. Use o modo de demonstração.')
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { display_name: displayName.trim() } },
  })
  if (error) throw error
  return data
}

export async function updateOwnPassword(password: string) {
  if (!supabase) throw new Error('Supabase ainda não conectado.')
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw error
}

export async function signOut() { await supabase?.auth.signOut() }

export async function currentProfile(): Promise<Profile | null> {
  if (!supabase) return null
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data, error } = await supabase.from('profiles').select('id, display_name, role, email').eq('id', user.id).single()
  if (error) throw error
  return data as Profile
}

export async function loadAppData(profile: Profile): Promise<AppData> {
  if (!supabase) throw new Error('Supabase não configurado')
  const [{ data: assignments, error: leadError }, { data: clients, error: clientError }, { data: notices }, { data: campaigns }, { data: orders }] = await Promise.all([
    supabase.from('lead_assignments').select('id, assigned_to, assigned_date, status, real_objection, next_action, next_date, profiles!lead_assignments_assigned_to_fkey(display_name), leads(*)').eq('assigned_date', new Date().toISOString().slice(0, 10)).order('created_at'),
    supabase.from('clients').select('*, profiles!clients_referred_by_fkey(display_name), client_files(*)').order('name'),
    supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(30),
    profile.role === 'admin' ? supabase.from('campaigns').select('*, clients(name)').order('updated_at', { ascending: false }) : Promise.resolve({ data: [], error: null }),
    profile.role === 'admin' ? supabase.from('service_orders').select('*').order('discovered_at', { ascending: false }) : Promise.resolve({ data: [], error: null }),
  ])
  if (leadError) throw leadError
  if (clientError) throw clientError
  const leads = (assignments || []).map((row: any) => ({ ...row.leads, id: row.leads.id, assigned_to: row.assigned_to, assigned_name: row.profiles?.display_name || '', assigned_date: row.assigned_date, status: row.status, real_objection: row.real_objection, next_action: row.next_action, next_date: row.next_date }))
  const normalizedClients = (clients || []).map((row: any) => ({ ...row, referrer_name: row.profiles?.display_name, files: row.client_files || [] }))
  let finance = { mrr: 0, fixed_cost: 774.05, cash_balance: 0, active_clients: normalizedClients.filter((c: any) => c.status === 'active').length, commissions: 0 }
  if (profile.role === 'admin') {
    const { data } = await supabase.from('finance_snapshots').select('*').order('snapshot_date', { ascending: false }).limit(1).maybeSingle()
    if (data) finance = data
  } else {
    finance.commissions = normalizedClients.filter((c: any) => c.status === 'active').length * 50
  }
  return {
    profile, leads, clients: normalizedClients, finance,
    campaigns: (campaigns || []).map((row: any) => ({ ...row, client_name: row.clients?.name || '' })),
    orders: (orders || []).map((row: any) => ({ ...row, age_days: Math.floor((Date.now() - new Date(row.discovered_at).getTime()) / 86400000) })),
    notices: notices || [],
  }
}

export async function updateLeadStatus(id: string, status: string, fields: Record<string, string | null> = {}) {
  if (!supabase) return
  const { error } = await supabase.from('lead_assignments').update({ status, ...fields, updated_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function getClientFileUrl(storagePath: string) {
  if (!supabase) return null
  const { data, error } = await supabase.storage.from('client-files').createSignedUrl(storagePath, 60)
  if (error) throw error
  return data.signedUrl
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
}

export async function subscribeToPush(userId: string) {
  if (!supabase || !('serviceWorker' in navigator) || !('PushManager' in window)) throw new Error('Push indisponível neste navegador ou no modo demonstração.')
  const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY as string
  if (!vapid) throw new Error('Chave pública de notificações ainda não configurada.')
  const registration = await navigator.serviceWorker.ready
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Permissão de notificação não concedida.')
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapid) })
  const json = subscription.toJSON()
  const { error } = await supabase.from('push_subscriptions').upsert({ user_id: userId, endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth, user_agent: navigator.userAgent }, { onConflict: 'endpoint' })
  if (error) throw error
}
